import { useEffect, useState } from 'react';
import { TYPE_LABELS, type Account } from '../../core/accounts';
import { buildTree, type Category } from '../../core/categories';
import { todayKey } from '../../core/dates';
import { INR_RATE_PPM, type Transaction, type TransactionInput, type TransactionType } from '../../core/transactions';
import { getUsdRate } from '../../data/exchangeRates';
import { AmountField, readAmount } from './AmountField';

interface Props {
  accounts: Account[];
  categories: Category[];
  tags: { id: string; name: string }[];
  defaultAccountId?: string | null;
  defaultCurrency?: 'INR' | 'USD';
  /** Present when editing; absent when adding. */
  editing?: Transaction | null;
  /** Compact single column on phone, per FR-TXN-15. */
  compact?: boolean;
  onSave: (input: TransactionInput) => Promise<void> | void;
  onCancel?: () => void;
  onDelete?: () => void;
}

const TYPE_OPTIONS: { value: TransactionType; label: string; glyph: string }[] = [
  { value: 'expense', label: 'Expense', glyph: '⊖' },
  { value: 'income', label: 'Income', glyph: '⊕' },
  { value: 'transfer', label: 'Transfer', glyph: '⇄' },
];

/**
 * The transaction form.
 *
 * WD-01: **amount-first**. Only date, account and amount are mandatory (FR-TXN-02), and
 * date and account both have defaults — so the amount is the only thing that genuinely
 * has to be typed, and it gets the top of the form and the initial focus. Everything
 * optional sits below a divider, and the rarely-used fields behind "More".
 */
export function TransactionForm({
  accounts,
  categories,
  tags,
  defaultAccountId,
  defaultCurrency = 'INR',
  editing,
  compact,
  onSave,
  onCancel,
  onDelete,
}: Props) {
  const active = accounts.filter((a) => !a.archived);

  const [type, setType] = useState<TransactionType>(editing?.type ?? 'expense');
  const [amount, setAmount] = useState(
    editing ? (editing.amountExpression ?? String(editing.amountPaise / 100)) : '',
  );
  const [dateKey, setDateKey] = useState(editing?.dateKey ?? todayKey());
  const [accountId, setAccountId] = useState(
    editing?.accountId ?? defaultAccountId ?? active[0]?.id ?? '',
  );
  const [toAccountId, setToAccountId] = useState(editing?.toAccountId ?? '');
  const [categoryId, setCategoryId] = useState(editing?.categoryId ?? '');
  const [subcategoryId, setSubcategoryId] = useState(editing?.subcategoryId ?? '');
  const [description, setDescription] = useState(editing?.description ?? '');
  const [currency, setCurrency] = useState<'INR' | 'USD'>(editing?.currency ?? defaultCurrency);
  const [payee, setPayee] = useState(editing?.payee ?? '');
  const [referenceNo, setReferenceNo] = useState(editing?.referenceNo ?? '');
  const [notes, setNotes] = useState(editing?.notes ?? '');
  const [tagIds, setTagIds] = useState<string[]>(editing?.tagIds ?? []);
  const [isBusiness, setIsBusiness] = useState(editing?.isBusiness ?? false);
  const [isReceivable, setIsReceivable] = useState(Boolean(editing?.receivableId));
  const [showMore, setShowMore] = useState(false);
  const [saving, setSaving] = useState(false);
  const [rate, setRate] = useState<{ ratePpm: number; stale: boolean }>({
    ratePpm: editing?.ratePpm ?? INR_RATE_PPM,
    stale: editing?.rateIsStale ?? false,
  });

  // Fetch a rate only when one is actually needed, and never block the form on it.
  useEffect(() => {
    if (currency !== 'USD') {
      setRate({ ratePpm: INR_RATE_PPM, stale: false });
      return;
    }
    let cancelled = false;
    void getUsdRate().then((result) => {
      if (!cancelled) setRate({ ratePpm: result.ratePpm, stale: result.stale });
    });
    return () => {
      cancelled = true;
    };
  }, [currency]);

  const amountState = readAmount(amount);
  const isTransfer = type === 'transfer';
  const canSave =
    amountState.paise !== null &&
    accountId !== '' &&
    (!isTransfer || (toAccountId !== '' && toAccountId !== accountId));

  const expenseTree = buildTree(categories, type === 'income' ? 'income' : 'expense');
  const subcategories = expenseTree.find((n) => n.category.id === categoryId)?.children ?? [];

  async function submit() {
    if (!canSave || amountState.paise === null) return;
    setSaving(true);
    try {
      await onSave({
        type,
        dateKey,
        accountId,
        amountPaise: amountState.paise,
        amountExpression: amountState.isExpression ? amount : null,
        toAccountId: isTransfer ? toAccountId : null,
        currency,
        ratePpm: rate.ratePpm,
        rateIsStale: currency === 'USD' ? rate.stale : false,
        categoryId: isTransfer ? null : categoryId || null,
        subcategoryId: isTransfer ? null : subcategoryId || null,
        description: description.trim() || null,
        payee: payee.trim() || null,
        referenceNo: referenceNo.trim() || null,
        notes: notes.trim() || null,
        tagIds,
        isBusiness,
        // FR-RCV-06: a checkbox at entry decides gift/expense versus receivable.
        receivableId: isReceivable ? (editing?.receivableId ?? 'pending') : null,
      });
      if (!editing) resetForNextEntry();
    } finally {
      setSaving(false);
    }
  }

  /**
   * Clear the amount and description but keep date, account and type.
   *
   * Entering ten rows in a row is the normal case, and they usually share an account and
   * a date. Resetting everything would make the second entry as slow as the first.
   */
  function resetForNextEntry() {
    setAmount('');
    setDescription('');
    setPayee('');
    setReferenceNo('');
    setNotes('');
    setTagIds([]);
  }

  return (
    <div className={compact ? 'txn-form compact' : 'txn-form'}>
      <AmountField
        value={amount}
        onChange={setAmount}
        autoFocus={compact}
        large={compact}
        currency={currency}
      />

      <div className="type-toggle" role="group" aria-label="Type">
        {TYPE_OPTIONS.map((option) => (
          <button
            key={option.value}
            type="button"
            aria-pressed={type === option.value}
            onClick={() => setType(option.value)}
          >
            <span aria-hidden="true">{option.glyph}</span> {option.label}
          </button>
        ))}
      </div>

      <label className="field">
        <span>Date</span>
        <input type="date" value={dateKey} onChange={(e) => setDateKey(e.target.value)} />
      </label>

      <label className="field">
        <span>{isTransfer ? 'From account' : 'Account'}</span>
        <select value={accountId} onChange={(e) => setAccountId(e.target.value)}>
          {active.map((a) => (
            <option key={a.id} value={a.id}>
              {a.name} · {TYPE_LABELS[a.type]}
            </option>
          ))}
        </select>
      </label>

      {isTransfer && (
        <label className="field">
          <span>To account</span>
          <select value={toAccountId} onChange={(e) => setToAccountId(e.target.value)}>
            <option value="">Choose…</option>
            {active
              .filter((a) => a.id !== accountId)
              .map((a) => (
                <option key={a.id} value={a.id}>
                  {a.name} · {TYPE_LABELS[a.type]}
                </option>
              ))}
          </select>
          <small className="muted">
            One record, both sides. Excluded from income and expense totals.
          </small>
        </label>
      )}

      <hr className="divider" />

      {/* Transfers carry no category — they are not spending. */}
      {!isTransfer && (
        <>
          <label className="field">
            <span>Category</span>
            <select
              value={categoryId}
              onChange={(e) => {
                setCategoryId(e.target.value);
                setSubcategoryId('');
              }}
            >
              <option value="">None</option>
              {expenseTree
                .filter((n) => n.category.id !== '__orphans__')
                .map((n) => (
                  <option key={n.category.id} value={n.category.id}>
                    {n.category.name}
                  </option>
                ))}
            </select>
          </label>

          {subcategories.length > 0 && (
            <label className="field">
              <span>Subcategory</span>
              <select value={subcategoryId} onChange={(e) => setSubcategoryId(e.target.value)}>
                <option value="">None</option>
                {subcategories.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </label>
          )}
        </>
      )}

      <label className="field">
        <span>Description</span>
        <input
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Optional"
        />
      </label>

      <button type="button" className="btn-link" onClick={() => setShowMore((v) => !v)}>
        {showMore ? '⌃ Less' : '⌄ More'}
      </button>

      {showMore && (
        <div className="more-fields">
          <label className="field">
            <span>Currency</span>
            <select value={currency} onChange={(e) => setCurrency(e.target.value as 'INR' | 'USD')}>
              <option value="INR">INR — ₹</option>
              <option value="USD">USD — $</option>
            </select>
            {currency === 'USD' && (
              <small className={rate.stale ? 'error' : 'muted'}>
                Rate {(rate.ratePpm / 1_000_000).toFixed(3)} INR/USD
                {rate.stale && ' — could not reach a rate source; using the last known rate'}
                . Frozen onto this transaction.
              </small>
            )}
          </label>

          <label className="field">
            <span>Payee</span>
            <input value={payee} onChange={(e) => setPayee(e.target.value)} />
          </label>

          <label className="field">
            <span>Reference no.</span>
            <input value={referenceNo} onChange={(e) => setReferenceNo(e.target.value)} />
          </label>

          <label className="field">
            <span>Notes</span>
            <textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} />
          </label>

          {tags.length > 0 && (
            <div className="field">
              <span>Tags</span>
              <ul className="chips">
                {tags.map((tag) => (
                  <li key={tag.id}>
                    <button
                      type="button"
                      className={tagIds.includes(tag.id) ? 'chip selected' : 'chip'}
                      onClick={() =>
                        setTagIds((current) =>
                          current.includes(tag.id)
                            ? current.filter((t) => t !== tag.id)
                            : [...current, tag.id],
                        )
                      }
                    >
                      {tag.name}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <label className="checkbox">
            <input
              type="checkbox"
              checked={isBusiness}
              onChange={(e) => setIsBusiness(e.target.checked)}
            />
            Business
          </label>

          {!isTransfer && type === 'expense' && (
            <label className="checkbox">
              <input
                type="checkbox"
                checked={isReceivable}
                onChange={(e) => setIsReceivable(e.target.checked)}
              />
              Money lent, not spent
              <small className="muted">
                Keeps it out of expenses — it belongs to receivables, and counting it
                would make spending fall when you are repaid.
              </small>
            </label>
          )}
        </div>
      )}

      <div className="row form-actions">
        <button
          type="button"
          className="btn btn-primary"
          disabled={!canSave || saving}
          onClick={() => void submit()}
        >
          {saving ? 'Saving…' : editing ? 'Save changes' : 'Save'}
        </button>
        {onCancel && (
          <button type="button" className="btn" onClick={onCancel}>
            Cancel
          </button>
        )}
        {editing && onDelete && (
          <button type="button" className="btn-link danger" onClick={onDelete}>
            Delete
          </button>
        )}
      </div>
    </div>
  );
}
